/**
 * The tenant's own Twilio account — BYO (Prompt 2.9): configure / validate /
 * detach (tenant: tenant.telephony_byo.manage; platform: platform.telephony.manage),
 * import its numbers, re-apply drifted webhooks, and the platform tenant
 * Telephony overview. Secrets are only ever sent, never returned (the SID
 * comes back masked).
 */
import { del, get, post, put } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type { PhoneNumber } from './phoneNumbers.service';

export type ByoAccountStatus = 'pending' | 'active' | 'invalid' | 'suspended' | 'closed';

export interface ByoAccount {
  configured: boolean;
  status: ByoAccountStatus | null;
  /** Masked, e.g. "AC…1a2b". */
  accountSid: string | null;
  configuredBy: 'platform' | 'tenant' | null;
  lastValidatedAt: string | null;
  validationError: string | null;
  numbers: number;
  driftedNumbers: number;
  /** Who bills what on BYO numbers (shown wherever BYO is configured). */
  disclosure: string;
}

export interface ByoCredentials {
  accountSid: string;
  authToken: string;
}

export interface ByoIncomingNumber {
  sid: string;
  e164: string;
  friendlyName: string;
  country: string | null;
  voice: boolean;
  /** Where the number's voice webhook points today (null = nowhere). */
  voiceUrlHost: string | null;
  imported: boolean;
  phoneNumberId: string | null;
}

export type ImportOutcome =
  'imported' | 'already_imported' | 'not_found' | 'not_voice' | 'in_use' | 'failed';

export interface ImportNumbersResult {
  results: Array<{ sid: string; outcome: ImportOutcome; number?: PhoneNumber; error?: string }>;
  imported: number;
}

export interface DetachResult {
  detached: boolean;
  restored: number;
  /** Numbers left pointing at Orlenn (forced detach). */
  notRestored: string[];
}

export interface PlatformTelephonyOverview {
  tenantId: string;
  managed: {
    configured: boolean;
    status: string | null;
    accountSid: string | null;
    lastValidatedAt: string | null;
  };
  byo: ByoAccount;
  numbers: Array<PhoneNumber & { ownership: 'managed' | 'byo' }>;
  usage: {
    period: string;
    meters: Array<{ meterCode: string; quantity: number; amountUsd: number }>;
  };
}

/** Same operations, for the tenant itself or (platform) for a given tenant. */
export interface ByoAccountApi {
  get(): Promise<ByoAccount>;
  configure(creds: ByoCredentials): Promise<ByoAccount>;
  validate(): Promise<ByoAccount>;
  detach(force?: boolean): Promise<DetachResult>;
}

const detachParams = (force?: boolean) => ({ params: force ? { force: 'true' } : {} });

export const byoTelephonyService = {
  /** The signed-in tenant's BYO account. */
  self: {
    get: () => get<ByoAccount>(endpoints.telephony.byo),
    configure: (creds: ByoCredentials) => put<ByoAccount>(endpoints.telephony.byo, creds),
    validate: () => post<ByoAccount>(endpoints.telephony.byoValidate),
    detach: (force?: boolean) => del<DetachResult>(endpoints.telephony.byo, detachParams(force)),
  } satisfies ByoAccountApi,

  /** A tenant's BYO account, managed by a platform admin. */
  forTenant(tenantId: string): ByoAccountApi {
    const e = endpoints.platformTenantTelephony;
    return {
      get: () => get<PlatformTelephonyOverview>(e.overview(tenantId)).then((o) => o.byo),
      configure: (creds) => put<ByoAccount>(e.byo(tenantId), creds),
      validate: () => post<ByoAccount>(e.byoValidate(tenantId)),
      detach: (force) => del<DetachResult>(e.byo(tenantId), detachParams(force)),
    };
  },

  overview(tenantId: string): Promise<PlatformTelephonyOverview> {
    return get<PlatformTelephonyOverview>(endpoints.platformTenantTelephony.overview(tenantId));
  },

  incomingNumbers(): Promise<ByoIncomingNumber[]> {
    return get<ByoIncomingNumber[]>(endpoints.telephony.byoIncomingNumbers);
  },

  importNumbers(sids: string[]): Promise<ImportNumbersResult> {
    return post<ImportNumbersResult>(endpoints.phoneNumbers.import, { sids });
  },

  reapplyWebhooks(phoneNumberId: string): Promise<PhoneNumber> {
    return post<PhoneNumber>(endpoints.phoneNumbers.reapplyWebhooks(phoneNumberId));
  },
} as const;
